import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { LifeBuoy, LogOut } from 'lucide-react';
import { PublicAuthShell } from '@/components/layout/PublicAuthShell.js';
import { CHAVE_SUPORTE, api, extractError, lerSuporte, setAccessToken, type InfoSuporte } from '@/lib/api.js';

/**
 * Modo suporte: alguém da equipe, pelo HAOps, vendo a conta de um aluno.
 * Servidor em src/lib/suporte.ts — ticket de uso único, token sem renovação,
 * somente leitura, auditado.
 */

type RespostaEntrar = {
  accessToken: string;
  user: { email: string };
  suporte: Omit<InfoSuporte, 'aluno_email'>;
};

/** `/suporte/entrar#ticket=…` — aberto pelo botão "Acessar como" do HAOps. */
export function SuporteEntrarPage() {
  const nav = useNavigate();
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const ticket = new URLSearchParams(window.location.hash.slice(1)).get('ticket');
    // Tira o ticket da barra de endereço já: ele é de uso único, mas não
    // precisa ficar no histórico do navegador.
    window.history.replaceState(null, '', '/suporte/entrar');
    if (!ticket) {
      setErro('Link de suporte sem ticket. Gere outro no HAOps.');
      return;
    }
    let vivo = true;
    api
      .post<RespostaEntrar>('/auth/suporte/entrar', { ticket })
      .then(({ data }) => {
        if (!vivo) return;
        const info: InfoSuporte = { ...data.suporte, aluno_email: data.user.email };
        sessionStorage.setItem(CHAVE_SUPORTE, JSON.stringify({ token: data.accessToken, info }));
        setAccessToken(data.accessToken);
        nav('/inicio', { replace: true });
      })
      .catch((e) => vivo && setErro(extractError(e).message));
    return () => {
      vivo = false;
    };
  }, [nav]);

  return (
    <PublicAuthShell title="Modo suporte" subtitle="Abrindo a conta do aluno…">
      <p className="text-sm text-ha-subtle">{erro ?? 'Conferindo o link de suporte…'}</p>
    </PublicAuthShell>
  );
}

export function SuporteEncerradoPage() {
  return (
    <PublicAuthShell title="Sessão de suporte encerrada" subtitle="O acesso à conta do aluno terminou.">
      <p className="text-sm text-ha-subtle">
        Para acessar de novo, gere outro link em HAOps. Pode fechar esta aba.
      </p>
    </PublicAuthShell>
  );
}

/** Encerra no servidor e limpa a aba. Usado pela faixa e pelo menu do usuário. */
export async function sairDoSuporte() {
  try {
    await api.post('/auth/suporte/sair');
  } catch {
    /* já vencida ou encerrada: o objetivo era sair */
  }
  sessionStorage.removeItem(CHAVE_SUPORTE);
  setAccessToken(null);
  window.location.href = '/suporte/encerrado';
}

function minutosAte(iso: string) {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 60_000));
}

/**
 * A faixa que não deixa ninguém esquecer de quem é a conta aberta. Fixa no
 * topo, acima de tudo, enquanto durar a sessão.
 */
export function FaixaSuporte() {
  const sessao = lerSuporte();
  const [restante, setRestante] = useState(() => (sessao ? minutosAte(sessao.info.expira_em) : 0));

  useEffect(() => {
    if (!sessao) return;
    const t = setInterval(() => setRestante(minutosAte(sessao.info.expira_em)), 30_000);
    return () => clearInterval(t);
  }, [sessao?.info.expira_em]);

  if (!sessao) return null;
  const { info } = sessao;

  return (
    <div
      role="status"
      className="sticky top-0 z-[60] flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 text-xs font-semibold"
      style={{ background: '#ffc800', color: '#111' }}
    >
      <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden />
      <span>
        Modo suporte — você está vendo a conta de <strong>{info.aluno_email}</strong>. Somente leitura.
      </span>
      <span className="opacity-75">
        Aberto por {info.operador_nome || info.operador_email} · termina em {restante} min
      </span>
      <button
        type="button"
        onClick={() => void sairDoSuporte()}
        className="ml-auto inline-flex items-center gap-1 rounded border border-black/30 px-2 py-1 hover:bg-black/10"
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden /> Sair do modo suporte
      </button>
    </div>
  );
}

type Acesso = { quem: string; motivo: string; inicio: string; fim: string };

/**
 * No perfil do aluno: quem da equipe entrou na conta dele, quando e por quê.
 * É o que separa suporte de vigilância — dito para quem importa.
 */
export function AcessosDeSuporte() {
  const q = useQuery({
    queryKey: ['acessos-de-suporte'],
    queryFn: async () => (await api.get<{ acessos: Acesso[] }>('/auth/suporte/acessos')).data.acessos,
  });
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

  return (
    <section className="mt-10 max-w-2xl">
      <h2 className="mb-1 text-sm font-bold">Acessos de suporte</h2>
      <p className="mb-3 text-xs text-ha-subtle">
        Quando alguém da equipe Hack precisa ver o que você vê para ajudar, entra na sua conta em modo
        somente leitura, por até 30 minutos. Cada acesso fica registrado aqui, com o motivo.
      </p>
      {q.isLoading ? (
        <p className="text-xs text-ha-muted">Carregando…</p>
      ) : !q.data?.length ? (
        <p className="text-xs text-ha-muted">Nenhum acesso de suporte à sua conta.</p>
      ) : (
        <ul className="divide-y divide-ha-border2 rounded-card border border-ha-border2 text-sm">
          {q.data.map((a) => (
            <li key={a.inicio} className="px-4 py-3">
              <div className="font-semibold">{a.quem}</div>
              <div className="text-xs text-ha-subtle">
                {fmt(a.inicio)} – {fmt(a.fim)}
              </div>
              <div className="mt-1 text-xs">{a.motivo}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

